import { prefersReducedMotion } from '@oo/theme';

// Maskottchen am Warenkorb: Standbild sofort, die WebGL-Figur lädt erst nach der Seite. Ohne WebGL, bei
// „weniger Bewegung“, Pause (.oo-hw-paused) oder je nach mascot_motion bleibt das Standbild. Fängt keine Klicks ab.

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
// mascot_motion „desktop“: hier durchgehend, auf Handys nur kurz beim Hinzufügen zum Warenkorb (Akku)
const bigScreen = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
const root = document.documentElement;
// Einzel-Animationen liegen in Ebenen; spätere Ebenen überschreiben frühere.
const SLOTS = ['blink', 'idle', 'action'];
const PRIORITY = { winken: 1, umschauen: 1, freude: 2, jubel: 3 };
const FADE_IN = 0.18;
const FADE_OUT = 0.32;
// Ruhiges Atmen mit höchstens 30 Bildern/s (etwas Spielraum für schwankende Bildabstände)
const IDLE_FRAME = 1000 / 30 - 4;
// So lange nach Beginn eines Übergangs am Warenkorb die Aussparung jedes Bild neu messen
const CART_MOTION = 400;

const random = (min, max) => min + Math.random() * (max - min);
// Knopf „Effekte anhalten“ der Halloween Edition (oo-halloween.js)
const paused = () => root.classList.contains('oo-hw-paused');

class OoMascot extends HTMLElement {
  connectedCallback() {
    this.cartLink = this.parentElement?.querySelector('.oo-header__cart') || null;
    this.visible = false;
    this.animate = false;
    this.layers = [];
    this.hovered = false;
    this.hoverWeight = 0;
    this.clock = 0;
    this.box = null;
    this.boxUntil = 0;
    this.reacting = false;
    this.reaction = null;
    this.count = Number(this.dataset.count) || 0;
    this.total = Number(this.dataset.total) || 0;

    this.onFrame = (now) => this.frame(now);
    this.onVisibility = () => this.wake();
    this.onPreference = () => this.refresh();
    this.onCart = (event) => this.cartChanged(event.detail || {});
    this.onBoxChange = () => (this.box = null);
    this.onCartMotion = (event) => {
      this.box = null;
      if (event.type === 'transitionrun') this.boxUntil = performance.now() + CART_MOTION;
    };
    this.cartEvents = [
      ['pointerenter', () => this.setHover(true)],
      ['pointerleave', () => this.setHover(false)],
      ['focusin', () => this.setHover(true)],
      ['focusout', (event) => !this.cartLink.contains(event.relatedTarget) && this.setHover(false)],
      ['transitionrun', this.onCartMotion],
      ['transitionend', this.onCartMotion],
      ['transitioncancel', this.onCartMotion],
    ];

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.wake();
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.onVisibility);
    document.addEventListener('oo:cart:changed', this.onCart);
    document.addEventListener('oo:hw-paused', this.onPreference);
    reducedMotion.addEventListener('change', this.onPreference);
    bigScreen.addEventListener('change', this.onPreference);
    window.addEventListener('resize', this.onBoxChange, { passive: true });
    window.addEventListener('scroll', this.onBoxChange, { passive: true });
    for (const [type, listener] of this.cartEvents) this.cartLink?.addEventListener(type, listener);
    this.whenIdle(() => this.refresh());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('oo:cart:changed', this.onCart);
    document.removeEventListener('oo:hw-paused', this.onPreference);
    reducedMotion.removeEventListener('change', this.onPreference);
    bigScreen.removeEventListener('change', this.onPreference);
    window.removeEventListener('resize', this.onBoxChange);
    window.removeEventListener('scroll', this.onBoxChange);
    for (const [type, listener] of this.cartEvents) this.cartLink?.removeEventListener(type, listener);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    // Erst abmelden: destroy() verwirft den Kontext selbst und löst sonst das Verlust-Ereignis aus.
    this.canvas?.removeEventListener('webglcontextlost', this.onContextLost);
    this.stage?.destroy();
    this.stage = null;
    this.canvas?.remove();
    this.canvas = null;
  }

  // Erst nach der Seite und in einer Leerlaufphase laden, damit nichts den Seitenaufbau bremst
  whenIdle(callback) {
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(callback, { timeout: 2500 }) : setTimeout(callback, 400));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  refresh() {
    if (!this.isConnected) return;
    this.animate = (this.allowed() || this.reacting) && !prefersReducedMotion() && !paused();
    if (this.animate && !this.stage && !this.loading && !this.failed) this.load();
    this.classList.toggle('is-live', Boolean(this.stage) && this.animate);
    this.wake();
  }

  // Theme-Einstellung mascot_motion: always, desktop (Standard) oder never
  allowed() {
    const mode = this.dataset.motion;
    if (mode === 'never') return false;
    if (mode === 'always') return true;
    return bigScreen.matches && navigator.connection?.saveData !== true;
  }

  // „desktop“ auf Handys: lädt erst beim Hinzufügen, spielt die Reaktion, danach wieder Standbild
  reactOnly() {
    const mode = this.dataset.motion;
    if (mode === 'never' || mode === 'always' || bigScreen.matches || this.failed) return false;
    return navigator.connection?.saveData !== true && !prefersReducedMotion() && !paused();
  }

  async load() {
    this.loading = true;
    const canvas = document.createElement('canvas');
    canvas.className = 'oo-mascot__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    try {
      const [gl, buffer] = await Promise.all([
        // Der Spieler (WebGL) kommt erst jetzt dazu; ohne Animation wird er nie geladen
        import('@oo/mascot-gl'),
        fetch(this.dataset.src).then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.arrayBuffer();
        }),
      ]);
      const figure = gl.parseGlb(buffer);
      // Ohne diese beiden scheitert sonst die Schleife
      if (!figure.animations.ruhe || !figure.animations.hover) throw new Error('animations');
      const image = await this.decode(figure.image);
      if (!this.isConnected) return;
      this.append(canvas);
      this.resize(canvas);
      this.stage = new gl.Stage(canvas, figure, image);
      this.pose = new gl.Pose(figure);
      this.animations = figure.animations;
      this.canvas = canvas;
      // Kontext verloren (GPU-Neustart, Speicherdruck): zurück zum Standbild
      this.onContextLost = () => {
        if (this.canvas !== canvas) return;
        this.failed = true;
        this.stage = null;
        this.resizeObserver?.disconnect();
        canvas.remove();
        this.canvas = null;
        this.refresh();
      };
      canvas.addEventListener('webglcontextlost', this.onContextLost);
      this.resizeObserver = new ResizeObserver((entries) => {
        this.box = null;
        if (entries.some((entry) => entry.target === canvas)) this.resize(canvas);
        else this.render();
      });
      this.resizeObserver.observe(canvas);
      if (this.cartLink) this.resizeObserver.observe(this.cartLink);
      this.planTimers();
      this.render();
      // Sichtbarkeit neu messen: Die erste Meldung kam evtl. vor dem fertigen Layout
      this.observer.unobserve(this);
      this.observer.observe(this);
    } catch {
      // Kein WebGL, Netzfehler oder alte Datei: Standbild bleibt, kein zweiter Versuch
      this.failed = true;
      this.reacting = false;
      this.reaction = null;
      this.canvas = null;
      this.stage?.destroy();
      this.stage = null;
      this.resizeObserver?.disconnect();
      canvas.remove();
    } finally {
      this.loading = false;
    }
    this.refresh();
  }

  async decode(blob) {
    const url = URL.createObjectURL(blob);
    try {
      const image = new Image();
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = url;
      });
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  resize(canvas) {
    // Gemessene Größe, höchstens doppelte Pixeldichte: scharf genug, wenig GPU-Last
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    this.box = null;
    // Neue Größe leert die Fläche: sofort neu zeichnen, auch wenn die Schleife gerade ein Bild auslässt
    if (this.stage) this.render();
  }

  // Begrüßung beim ersten Besuch der Sitzung, danach gelegentlich; Blinzeln und Umschauen unregelmäßig
  planTimers() {
    // Ohne Sitzungsspeicher lieber gar nicht begrüßen als auf jeder Seite erneut
    let greeted = true;
    try {
      greeted = sessionStorage.getItem('oo-mascot-greeted') === '1';
      sessionStorage.setItem('oo-mascot-greeted', '1');
    } catch {
      // Speicher gesperrt (z. B. privater Modus): bleibt bei „schon begrüßt“
    }
    this.next = {
      blink: random(1.5, 3.5),
      wave: greeted ? random(35, 70) : 1.4,
      look: random(12, 22),
    };
  }

  // this.animate setzt refresh(), damit hier pro Bild keine Medienabfrage nötig ist
  running() {
    return Boolean(this.stage) && this.animate && this.visible && document.visibilityState === 'visible' && !paused();
  }

  wake() {
    if (this.reaction && this.running()) {
      const name = this.reaction;
      this.reaction = null;
      this.play(name, 'action');
    }
    if (this.running() && !this.raf) {
      this.last = 0;
      this.raf = requestAnimationFrame(this.onFrame);
    } else if (!this.running() && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  }

  frame(now) {
    this.raf = 0;
    if (!this.running()) return;
    // Atmen mit höchstens 30 Bildern/s, alles andere mit voller Rate; ausgelassene Bilder zählen in delta mit.
    const busy = this.layers.length > 0 || this.hovered || this.hoverWeight > 0.001 || now < this.boxUntil;
    if (busy || !this.last || now - this.last >= IDLE_FRAME) {
      // Nach einer Pause (Tab im Hintergrund) nicht springen: höchstens 50 ms pro Bild
      const delta = this.last ? Math.min(0.05, (now - this.last) / 1000) : 0;
      this.last = now;
      this.step(delta);
      this.render();
      // Reaktion vorbei: Standbild; läuft die Figur weiter, plant wake() das nächste Bild
      if (this.reacting && !this.layers.some((layer) => layer.slot === 'action')) {
        this.reacting = false;
        this.refresh();
        return;
      }
    }
    this.raf = requestAnimationFrame(this.onFrame);
  }

  step(delta) {
    this.clock += delta;
    this.hoverWeight += ((this.hovered ? 1 : 0) - this.hoverWeight) * (1 - Math.exp(-delta / 0.14));

    for (const layer of this.layers) {
      layer.time += delta;
      const animation = this.animations[layer.name];
      if (layer.ending) {
        layer.weight -= delta / FADE_OUT;
      } else {
        const fadeIn = layer.fade ? Math.min(1, layer.time / FADE_IN) : 1;
        const fadeOut = layer.fade ? Math.min(1, (animation.duration - layer.time) / FADE_OUT) : 1;
        layer.weight = Math.max(0, Math.min(fadeIn, fadeOut));
      }
    }
    // Neue Ebenen starten mit Gewicht 0
    this.layers = this.layers.filter(
      (layer) => (!layer.ending || layer.weight > 0) && layer.time < this.animations[layer.name].duration,
    );

    const next = this.next;
    if (this.clock >= next.blink) {
      this.play('blinzeln', 'blink', false);
      // Ab und zu zweimal kurz hintereinander, sonst ruhig und unregelmäßig
      next.blink = this.clock + (Math.random() < 0.15 ? 0.4 : random(2.6, 6));
    }
    const calm = !this.reacting && !this.hovered && !this.layers.some((layer) => layer.slot === 'action');
    if (calm && this.clock >= next.wave) {
      this.play('winken', 'action');
      next.wave = this.clock + random(45, 90);
      next.look = Math.max(next.look, this.clock + 8);
    } else if (calm && this.clock >= next.look && !this.layers.some((layer) => layer.slot === 'idle')) {
      this.play('umschauen', 'idle');
      next.look = this.clock + random(14, 28);
    }
  }

  play(name, slot, fade = true) {
    if (!this.animations?.[name]) return;
    const running = this.layers.find((layer) => layer.slot === slot && !layer.ending);
    // Eine laufende Jubel-Animation wird nicht von einer kleineren Reaktion unterbrochen
    if (running && (PRIORITY[running.name] || 0) > (PRIORITY[name] || 0)) return;
    for (const layer of this.layers) {
      if (layer.slot === slot) layer.ending = true;
    }
    this.layers.push({ name, slot, fade, time: 0, weight: fade ? 0 : 1, ending: false });
    this.wake();
  }

  setHover(on) {
    this.hovered = on;
    if (on) this.layers.filter((layer) => layer.slot === 'idle').forEach((layer) => (layer.ending = true));
  }

  cartChanged({ itemCount, cart }) {
    const total = Number(cart?.total_price);
    const threshold = this.threshold();
    const before = this.total;
    const added = Number.isFinite(itemCount) && itemCount > this.count;
    if (Number.isFinite(itemCount)) this.count = itemCount;
    if (Number.isFinite(total)) this.total = total;
    // Jubel nur beim Überschreiten der Grenze, nicht bei jeder weiteren Änderung darüber
    const crossed = threshold > 0 && Number.isFinite(total) && total >= threshold && before < threshold;
    const name = crossed ? 'jubel' : added ? 'freude' : null;
    if (!name) return;
    if (this.running()) {
      this.play(name, 'action');
    } else if (this.reactOnly()) {
      if ((PRIORITY[name] || 0) >= (PRIORITY[this.reaction] || 0)) this.reaction = name;
      this.reacting = true;
      this.refresh();
    }
  }

  // Grenze in Cent ohne Wechselkurs, wie Versandleiste und Liquid (Shop-Währung EUR)
  threshold() {
    return Number(this.dataset.freeShipping) || 0;
  }

  render() {
    if (!this.stage) return;
    const { pose, animations } = this;
    pose.reset();
    // Bei der kurzen Reaktion am Handy schwingt nichts nach: Anfang und Ende gleichen so genau dem Standbild
    pose.apply(animations.ruhe, this.reacting ? 0 : this.clock % animations.ruhe.duration);
    for (const slot of SLOTS) {
      if (slot === 'action' && this.hoverWeight > 0.001) {
        pose.apply(animations.hover, this.clock % animations.hover.duration, this.hoverWeight);
      }
      for (const layer of this.layers) {
        if (layer.slot === slot) pose.apply(animations[layer.name], layer.time, layer.weight);
      }
    }
    pose.update();
    this.stage.draw(pose, this.cartBox());
  }

  // Oberkante des Warenkorbs in Canvas-Pixeln: Hintere Teile der Figur enden dort. Neu gemessen nur nach
  // Größenänderung, Scrollen oder solange der Warenkorb sich bewegt.
  cartBox() {
    if (!this.cartLink) return { top: 1e5 };
    if (this.box && performance.now() >= this.boxUntil) return this.box;
    const canvas = this.canvas.getBoundingClientRect();
    const scale = this.canvas.width / (canvas.width || 1);
    this.box = { top: (this.cartLink.getBoundingClientRect().top - canvas.top) * scale };
    return this.box;
  }
}

if (!customElements.get('oo-mascot')) customElements.define('oo-mascot', OoMascot);
